<script setup lang="ts">
import { computed } from 'vue';
import { useSelectableList, type SelectableItem } from '../../libs/useSelectableList';

defineOptions({ inheritAttrs: false });

export interface SegmentedItem extends SelectableItem<string> {
  label: string;
}

interface Props {
  items: SegmentedItem[];
  modelValue: string;
  size?: 'sm' | 'md';
  circle?: boolean;
  showContent?: boolean;
  variant?: 'default' | 'plain';
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
  (e: 'change', value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  showContent: true,
  size: 'md',
  circle: false,
  variant: 'default',
});
const emit = defineEmits<Emits>();

const model = computed<string>({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
});

const { activeKey, select } = useSelectableList<string>({
  modelValue: model,
  items: computed(() => props.items),
  onChange: v => emit('change', v),
});

function onClick(item: SegmentedItem) {
  select(item.key);
}
</script>

<template>
  <div
    :class="[
      'segmented',
      `segmented--${size || 'md'}`,
      `segmented--${variant || 'default'}`,
      { 'segmented--circle': circle },
    ]"
    role="tablist"
    v-bind="$attrs"
  >
    <button
      v-for="item in items"
      :key="item.key"
      class="segmented__item"
      :class="{
        'segmented__item--active': activeKey === item.key,
        'segmented__item--disabled': item.disabled,
      }"
      type="button"
      role="tab"
      :aria-selected="activeKey === item.key"
      :aria-disabled="item.disabled || undefined"
      @click="onClick(item)"
    >
      <slot name="label" :item="item" :active="activeKey === item.key">
        {{ item.label }}
      </slot>
    </button>
  </div>
  <div class="segmented__content" v-if="showContent">
    <slot :active-key="activeKey" />
  </div>
</template>

<style lang="scss" scoped>
.segmented {
  display: inline-flex;
  padding: 2px;
  background: var(--x-back-4);
  border-radius: var(--x-radius-medium);
}

.segmented--sm {
  gap: 2px;
}
.segmented--md {
  gap: 4px;
}

.segmented__item {
  appearance: none;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: var(--x-text-5);
  cursor: pointer;
  padding: 6px 14px;
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
  flex: 1;
  text-align: center;
}

.segmented--sm .segmented__item {
  padding: 4px 10px;
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);
}

.segmented__item--active {
  background: #fff;
  color: var(--x-text-1);
  font-weight: 600;
  border: 1px solid var(--x-back-5);
  box-shadow: var(--x-shadow);
}

.segmented__item--disabled {
  color: var(--x-text-6);
  cursor: not-allowed;
}

.segmented__content {
  margin-top: 8px;
}

.segmented--circle {
  border-radius: 16px;
  & > button {
    border-radius: 16px;
    &.segmented__item--active {
      box-shadow: var(--x-shadow);
    }
  }
}

.segmented--plain {
  padding: 0;
  background: transparent;
  gap: 4px;
  display: flex;
  align-items: center;

  .segmented__item {
    padding: 8px 16px;
    border-radius: 8px;
    color: var(--x-text-5);
    background: transparent;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .segmented__item--active {
    background: var(--x-back-4);
    color: var(--x-text-1);
    font-weight: 500;
    border: none;
    box-shadow: none;
  }

  .segmented__item:hover:not(.segmented__item--active):not(.segmented__item--disabled) {
    background: var(--x-back-4);
  }

  .segmented__item--disabled {
    cursor: not-allowed;
  }
}

.segmented--plain.segmented--sm .segmented__item {
  padding: 6px 12px;
  font-size: 13px;
}
</style>
